import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-circular-progress',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="circular-progress-container">
      <svg class="circular-progress" [attr.width]="size" [attr.height]="size" [attr.viewBox]="'0 0 ' + size + ' ' + size">
        <!-- Background circle -->
        <circle class="progress-bg"
                [attr.cx]="center"
                [attr.cy]="center"
                [attr.r]="radius"
                [attr.stroke-width]="strokeWidth"
                fill="none"
                [attr.stroke]="backgroundStroke"></circle>

        <!-- Progress circle -->
        <circle class="progress-ring"
                [attr.cx]="center"
                [attr.cy]="center"
                [attr.r]="radius"
                [attr.stroke-width]="strokeWidth"
                fill="none"
                [attr.stroke]="strokeColor"
                [attr.stroke-dasharray]="circumference"
                [attr.stroke-dashoffset]="dashOffset"
                stroke-linecap="round"
                transform="rotate(-90)"
                [attr.transform-origin]="center + ' ' + center"></circle>
      </svg>

      <!-- Center text -->
      <div class="progress-text">
        <div class="progress-percentage" [style.color]="strokeColor">{{ percentage }}%</div>
        <div class="progress-counts">{{ current }} / {{ total }}</div>
        <div class="progress-label" *ngIf="label">{{ label }}</div>
      </div>
    </div>
  `,
  styles: [`
    .circular-progress-container {
      position: relative;
      display: inline-block;
    }

    .circular-progress {
      transform: rotate(0deg);
    }

    .progress-ring {
      transition: stroke-dashoffset 0.5s cubic-bezier(0.4, 0, 0.2, 1),
                  stroke 0.3s ease;
    }

    .progress-text {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
    }

    .progress-percentage {
      font-size: 32px;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 4px;
    }

    .progress-counts {
      font-size: 16px;
      color: var(--text-color-secondary);
      font-weight: 500;
    }

    .progress-label {
      font-size: 12px;
      color: var(--text-color-secondary);
      text-transform: uppercase;
      margin-top: 4px;
      letter-spacing: 0.5px;
    }

    @media (max-width: 576px) {
      .progress-percentage {
        font-size: 28px;
      }
      .progress-counts {
        font-size: 14px;
      }
    }
  `]
})
export class CircularProgressComponent {
  @Input() size: number = 140;
  @Input() strokeWidth: number = 12;
  @Input() percentage: number = 0;
  @Input() current: number = 0;
  @Input() total: number = 0;
  @Input() label: string = '';

  get center(): number {
    return this.size / 2;
  }

  get radius(): number {
    return (this.size - this.strokeWidth) / 2;
  }

  get circumference(): number {
    return 2 * Math.PI * this.radius;
  }

  get dashOffset(): number {
    return this.circumference - (this.percentage / 100) * this.circumference;
  }

  get backgroundStroke(): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue('--bg-color-tertiary').trim() || '#e9ecef';
  }

  get strokeColor(): string {
    // Get computed CSS variable values from the document root
    const getThemeColor = (variable: string): string => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable).trim();
    };

    if (this.percentage === 0) {
      return getThemeColor('--border-color') || '#dee2e6';
    }
    if (this.percentage < 34) {
      return getThemeColor('--accent-warning') || '#ffc107';
    }
    if (this.percentage < 67) {
      return getThemeColor('--accent-info') || '#0d6efd';
    }
    if (this.percentage < 100) {
      return getThemeColor('--accent-primary') || '#198754';
    }
    return getThemeColor('--accent-primary') || '#198754';
  }
}
